/**
 * Purpose:
 *   Button component - composes icon, content, and spinner.
 *
 * Notes:
 *   - Accessible, theme-aware Button using CSS variables
 *   - States: Default / Hover / Disabled / Loading
 *   - When `iconOnly`, ensure you pass an `aria-label`
 */

'use client';

import React from 'react';
import { cx } from '@/util/functions/cx';
import { ButtonContent } from './ButtonContent';
import { ButtonSpinner } from './ButtonSpinner';
import { useButtonStyles } from './(hooks)/useButtonStyles';
import { buttonStyles } from './Button.styles';
import type { TButtonVariant, TButtonSize } from './Button.types';

export interface IButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: TButtonVariant;
  size?: TButtonSize;
  loading?: boolean;
  icon?: React.ReactNode;
  iconPosition?: 'leading' | 'trailing';
  iconOnly?: boolean;
  /** Demo only (e.g. Storybook): render hover appearance. Uses same styles as :hover. */
  forceHover?: boolean;
  /** Demo only (e.g. Storybook): render focus-visible ring. Uses same styles as focus-visible. */
  forceFocusVisible?: boolean;
  /** Rendered as `data-testid` on the native button (e2e / testing). */
  dataTestId?: string;
  /** Backward-compatible alias for test selectors on callers using native attribute form. */
  'data-testid'?: string;
}

export const Button = React.forwardRef<HTMLButtonElement, IButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      loading = false,
      icon,
      iconPosition = 'leading',
      iconOnly = false,
      disabled,
      forceHover = false,
      forceFocusVisible = false,
      dataTestId,
      'data-testid': legacyDataTestId,
      className,
      children,
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled || loading;
    const styles = useButtonStyles({
      variant,
      size,
      iconOnly,
      disabled: isDisabled,
      forceHover,
      forceFocusVisible,
    });

    return (
      <button
        ref={ref}
        className={cx(buttonStyles.base, styles.className, className)}
        aria-busy={loading || undefined}
        disabled={isDisabled}
        {...rest}
        data-testid={dataTestId ?? legacyDataTestId}
      >
        <ButtonSpinner loading={loading} size={size} />
        <ButtonContent
          loading={loading}
          icon={icon}
          iconPosition={iconPosition}
          iconOnly={iconOnly}
          size={size}
        >
          {children}
        </ButtonContent>
      </button>
    );
  }
);
Button.displayName = 'Button';
